import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppleIcon from "@mui/icons-material/Apple";
import WindowIcon from "@mui/icons-material/Window";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { captureMessage } from "@sentry/react";
import jsyaml from "js-yaml";
import {
  Stack,
  Button,
  Typography,
  Checkbox,
  AccordionGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Sheet,
  StepIndicator,
  styled,
} from "@financeable-com-au/financeable-ui";
import { envVariables } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { setSubmissionModalOpen, applicationSelector } from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, ApplicationState } from "@/store/slices/types/applicationFormSlice";
import { SubmissionStepper } from "@/components/SubmissionModal/SubmissionStepper";
import { useSubmissionSteps, useSubmission, useApplicationRouteType } from "@/components/SubmissionModal/hooks";
import { useFeatureFlags } from "@/utils/featureFlags/useFeatureFlags";
import { isLodgeAISupported } from "@/utils/lenderUtils";

const otaPaths = {
  mac: `${envVariables.VITE_LODGEAI_NATIVE_OTA_URL}/darwin`,
  windows: `${envVariables.VITE_LODGEAI_NATIVE_OTA_URL}/win32`,
} as const;

const DownloadButton = styled(Button)`
  justify-content: flex-start;
  border-color: ${({ theme }) => theme.palette.neutral[200]};

  &:disabled,
  &.Mui-disabled {
    color: ${({ theme }) => theme.palette.neutral[400]};

    & * {
      color: inherit;
    }
  }
`;

const CheckboxContainer = styled(Sheet)`
  padding: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Confirmation = () => {
  const { submissionModal } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  // LodgeAI submissions currently support the native desktop app only.
  const getDefaultLodgeAIMethod = (): "LOCAL" => {
    return "LOCAL";
  };

  const [lodgeAISubmissionMethod, setLodgeAISubmissionMethod] = useState<"LOCAL">(getDefaultLodgeAIMethod());
  const [isLodgeAIAppConfirmed, setIsLodgeAIAppConfirmed] = useState(false);
  const { isLodgeAIEnabled } = useFeatureFlags();

  // Download LodgeAI native apps.
  const [downloadPaths, setDownloadPaths] = useState<{ mac: string | null; windows: string | null }>({
    mac: null,
    windows: null,
  });

  useEffect(() => {
    const fetchDownloadUrls = async () => {
      try {
        const [macResult, winResult] = await Promise.allSettled([
          fetch(`${otaPaths.mac}/latest-universal.yml`, { cache: "no-store" }).then((res) => res.text()),
          fetch(`${otaPaths.windows}/latest.yml`, { cache: "no-store" }).then((res) => res.text()),
        ]);

        const macData = macResult.status === "fulfilled" ? (jsyaml.load(macResult.value) as { path?: string }) : null;
        const winData = winResult.status === "fulfilled" ? (jsyaml.load(winResult.value) as { path?: string }) : null;

        setDownloadPaths({
          mac: macData?.path ?? null,
          windows: winData?.path ?? null,
        });
      } catch (error) {
        captureMessage(`Failed to fetch LodgeAI download URLs: ${error}`);
      }
    };

    fetchDownloadUrls();
  }, []);

  const handleDownloadApp = (type: "mac" | "windows") => {
    // Construct URL to download the native app.
    const url = `${otaPaths[type]}/${downloadPaths[type]}`;
    window.open(url, "_blank");
  };

  // LodgeAI confirmation.
  const isLodgeAIConfirmation =
    isLodgeAIEnabled() &&
    isLodgeAISupported({ lodgeAI: submissionModal.lodgeAI }) &&
    submissionModal.submissionType === "browser";
  const { proceedSubmissionSteps } = useSubmissionSteps();
  const { handleSubmit } = useSubmission();
  const applicationRouteType = useApplicationRouteType();
  const dispatch = useDispatch();

  const handleCloseSubmissionModal = () => {
    dispatch(setSubmissionModalOpen({ isOpen: false }));
  };

  const handleConfirm = async () => {
    proceedSubmissionSteps({ activeStep: "confirmation" });
    handleSubmit(isLodgeAIConfirmation, lodgeAISubmissionMethod);
  };

  return (
    <>
      <Stack rowGap={10} padding={8} overflow="auto">
        <SubmissionStepper activeStep="confirmation" />
        <Stack rowGap={2}>
          <Typography component="h2" id="modal-title" level="title-md">
            Confirm submission
          </Typography>
          <Typography>
            You are submitting your {applicationRouteType} to{" "}
            <Typography component="span" textColor="text.primary" bold>
              '{submissionModal.lender}'
            </Typography>
            .
          </Typography>
          {isLodgeAIConfirmation && (
            <Stack rowGap={4}>
              <Typography>It will lodge automatically when a session becomes available.</Typography>
              {isLodgeAIConfirmation ? (
                <Stack rowGap={2}>
                  <AccordionGroup color="primary" size="sm" variant="outlined">
                    <Accordion defaultExpanded>
                      <AccordionSummary color="primary" variant="soft" isCondensed>
                        <Stack flexDirection="row" alignItems="center" columnGap={2}>
                          <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "var(--joy-palette-primary-200)" }} />
                          <Typography level="body-sm" bold>
                            Required: set up the desktop app
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails variant="plain" isCondensed>
                        <Stack rowGap={1}>
                          <Typography level="body-sm">
                            To submit, you'll need the LodgeAI desktop app running on this device.
                          </Typography>
                          <Typography level="body-xs">
                            Download it below for your operating system, unzip the file, and double-click the app icon
                            to launch it. That's it — come back here and confirm to start your submission.
                          </Typography>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </AccordionGroup>
                  <Sheet variant="outlined" color="primary" borderRadius="md" padding={3}>
                    <Stack rowGap={4}>
                      <Stack rowGap={2}>
                        <Stack flexDirection="row" alignItems="flex-start" columnGap={2}>
                          <StepIndicator color="primary" size="sm" variant="outlined">
                            1
                          </StepIndicator>
                          <Typography level="body-sm">Download LodgeAI</Typography>
                        </Stack>
                        <Stack flexDirection="row" columnGap={2} width="100%">
                          <DownloadButton
                            variant="secondary"
                            intent="neutral"
                            disabled={!downloadPaths.mac}
                            onClick={() => handleDownloadApp("mac")}
                            endDecorator={<ArrowDownwardIcon sx={{ fontSize: "1rem" }} />}
                            fullWidth
                          >
                            <Stack component="span" flexDirection="row" columnGap={2} alignItems="center" flexGrow={2}>
                              <AppleIcon />
                              <Stack alignItems="flex-start" textAlign="left" gap={0}>
                                <Typography component="span" bold>
                                  Mac
                                </Typography>
                                <Typography component="span" level="body-xs">
                                  macOS 12+ · Universal
                                </Typography>
                              </Stack>
                            </Stack>
                          </DownloadButton>
                          <DownloadButton
                            variant="secondary"
                            intent="neutral"
                            disabled={!downloadPaths.windows}
                            onClick={() => handleDownloadApp("windows")}
                            endDecorator={<ArrowDownwardIcon sx={{ fontSize: "1rem" }} />}
                            fullWidth
                          >
                            <Stack component="span" flexDirection="row" columnGap={2} alignItems="center" flexGrow={2}>
                              <WindowIcon />
                              <Stack alignItems="flex-start" textAlign="left" gap={0}>
                                <Typography component="span" bold>
                                  Windows
                                </Typography>
                                <Typography component="span" level="body-xs">
                                  Windows 10+ · x64
                                </Typography>
                              </Stack>
                            </Stack>
                          </DownloadButton>
                        </Stack>
                      </Stack>
                      <Stack rowGap={2}>
                        <Stack flexDirection="row" alignItems="flex-start" columnGap={2}>
                          <StepIndicator color="primary" size="sm" variant="outlined">
                            2
                          </StepIndicator>
                          <Typography level="body-sm">Verify it's installed</Typography>
                        </Stack>
                        <Stack rowGap={2}>
                          <CheckboxContainer variant="outlined" borderRadius="sm">
                            <Checkbox
                              label={
                                <Typography level="body-xs" textColor="neutral.900">
                                  I confirm that the LodgeAI desktop app is installed and running on this device.
                                </Typography>
                              }
                              size="sm"
                              checked={isLodgeAIAppConfirmed}
                              onChange={(event) => setIsLodgeAIAppConfirmed(event.target.checked)}
                            />
                          </CheckboxContainer>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Sheet>
                </Stack>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack
        display="grid"
        gridTemplateColumns={isLodgeAIConfirmation ? "minmax(0, 3fr) minmax(0, 2fr)" : undefined}
        columnGap={2}
        alignItems="center"
        bgcolor="neutral.100"
        borderTop="1px solid"
        borderColor="neutral.200"
        paddingX={8}
        paddingY={5}
      >
        {isLodgeAIConfirmation ? (
          <Typography level="body-xs">Tick the confirmation above to enable Confirm.</Typography>
        ) : null}
        <Stack flexDirection="row" columnGap={2} width="100%">
          <Button variant="secondary" onClick={handleCloseSubmissionModal} fullWidth>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleConfirm}
            disabled={isLodgeAIConfirmation && !isLodgeAIAppConfirmed}
          >
            Confirm
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export { Confirmation };
